/* eslint-disable import/no-cycle */
import { call, delay, put, takeLatest } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import * as types from '../../salesAgent/actionTypes';
import {
  setSalesAgentList,
  setSalesAgentDetails,
  setSalesAgentMerchants,
  createSalesAgentSuccess,
  createSalesAgentFailure,
  updateSalesAgentStatusSuccess,
  updateSalesAgentSuccess,
  updateSalesAgentFailure,
} from '../../salesAgent/actions';
import {
  getSalesAgentListApi,
  createSalesAgentApi,
  getSalesAgentDetailApi,
  getSalesAgentMerchantsApi,
  updateSalesAgentStatusApi,
  updateSalesAgentApi,
} from '../requests/salesAgentRequest';
import { startLoader, stopLoader } from '../../../utility/commonFunction';

function* getSalesAgentListHandler(action) {
  if (action.payload?.search) {
    yield delay(1000);
  }
  try {
    yield startLoader('transparent');
    const params = action?.payload || {};
    const { data } = yield call(getSalesAgentListApi, params);
    const salesAgentList = data?.status ? data?.data : {};
    if (salesAgentList) {
      yield put(setSalesAgentList(salesAgentList));
    }
    if (!data?.status && data?.errors?.message) {
      yield toast.error(data?.errors?.message);
    }
  } catch (error) {
    yield toast.error(error?.response?.data?.message || 'Failed to fetch sales agent list');
  } finally {
    yield stopLoader('transparent');
  }
}

function* createSalesAgentHandler(action) {
  try {
    yield startLoader('transparent');
    const { data } = yield call(createSalesAgentApi, action.payload);
    if (data?.status) {
      yield put(createSalesAgentSuccess(true));
      yield toast.success(data?.message || 'Sales agent created successfully');
      return data;
    }
    const errors = data?.errors ?? (data?.form_errors ? { form_errors: data.form_errors } : null);
    const errorMessage =
      errors?.message || errors?.form_errors?.email || errors?.form_errors?.name || 'Failed to create sales agent';
    yield put(createSalesAgentFailure(errors));
    yield toast.error(errorMessage);
  } catch (error) {
    const responseData = error?.response?.data;
    const formErrors = responseData?.form_errors ?? responseData?.errors?.form_errors;
    const failurePayload = formErrors ? { form_errors: formErrors } : error;
    const message =
      responseData?.message || responseData?.errors?.message || error?.message || 'Failed to create sales agent';
    yield put(createSalesAgentFailure(failurePayload));
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

function* getSalesAgentDetailHandler(action) {
  try {
    yield startLoader('transparent');
    const { data } = yield call(getSalesAgentDetailApi, action.payload.id);
    if (data?.status && data?.data) {
      yield put(setSalesAgentDetails(data.data));
    } else {
      yield toast.error(data?.errors?.message || 'Failed to fetch sales agent details');
    }
  } catch (error) {
    yield toast.error(error?.response?.data?.message || 'Failed to fetch sales agent details');
  } finally {
    yield stopLoader('transparent');
  }
}

function* updateSalesAgentStatusHandler(action) {
  try {
    yield startLoader('transparent');
    const { data } = yield call(updateSalesAgentStatusApi, action.payload.id, action.payload.status);

    if (data?.status) {
      yield put(
        updateSalesAgentStatusSuccess({
          id: action.payload.id,
          is_active: action.payload.status === 'active',
        }),
      );
      yield toast.success(data?.message || 'Sales agent status updated successfully');
    } else {
      yield toast.error(data?.errors?.message || data?.message || 'Failed to update sales agent status');
    }
  } catch (error) {
    yield toast.error(error?.response?.data?.message || 'Failed to update sales agent status');
  } finally {
    yield stopLoader('transparent');
  }
}

function* updateSalesAgentHandler(action) {
  try {
    yield startLoader('transparent');
    const { id, ...payload } = action.payload;
    const { data } = yield call(updateSalesAgentApi, id, payload);
    if (data?.status) {
      yield put(updateSalesAgentSuccess(true));
      yield toast.success(data?.message || 'Sales agent updated successfully');
    } else {
      const errorMessage = data?.errors?.message || data?.errors?.form_errors || 'Failed to update sales agent';
      yield put(updateSalesAgentFailure(errorMessage));
      yield toast.error(errorMessage);
    }
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.errors?.message ||
      error?.message ||
      'Failed to update sales agent';
    yield put(updateSalesAgentFailure(message));
    yield toast.error(message);
  } finally {
    yield stopLoader('transparent');
  }
}

function* getSalesAgentMerchantsHandler(action) {
  try {
    yield startLoader('transparent');
    const { id, page = 1, perPage = 10 } = action.payload || {};
    const { data } = yield call(getSalesAgentMerchantsApi, id, { page, perPage });
    const merchantList = data?.status ? data?.data : {};
    if (merchantList) {
      yield put(setSalesAgentMerchants(merchantList));
    }
    if (!data?.status && data?.errors?.message) {
      yield toast.error(data?.errors?.message);
    }
  } catch (error) {
    yield toast.error(error?.response?.data?.message || 'Failed to fetch sales agent merchants');
  } finally {
    yield stopLoader('transparent');
  }
}

export default function* salesAgentSaga() {
  yield takeLatest(types.GET_SALES_AGENT_LIST, getSalesAgentListHandler);
  yield takeLatest(types.CREATE_SALES_AGENT, createSalesAgentHandler);
  yield takeLatest(types.GET_SALES_AGENT_DETAILS, getSalesAgentDetailHandler);
  yield takeLatest(types.GET_SALES_AGENT_MERCHANTS, getSalesAgentMerchantsHandler);
  yield takeLatest(types.UPDATE_SALES_AGENT_STATUS, updateSalesAgentStatusHandler);
  yield takeLatest(types.UPDATE_SALES_AGENT, updateSalesAgentHandler);
}
